import PrimaryInnerGlowButton from '@/components/buttons/primary';
import Checkbox from '@/components/forms/Checkbox';
import InputField from '@/components/forms/InputField';
import PhoneField from '@/components/forms/PhoneField';
import SelectField from '@/components/forms/SelectField';
import TermsAndPrivacyNotice from '@/components/forms/TermsAndPrivacyNotice';
import Shared from '@/components/modals/shared';
import { CraftWebinarType } from '@/constants/craft';
import { GTM_EVENTS } from '@/constants/gtm';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
} from '@/constants/hubspot';
import { OPTIMIZELY_EVENTS } from '@/constants/optimizely';
import { OneTrustContext } from '@/context/onetrust';
import { getResourceFormId } from '@/lib/craft-utils';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import optimizely from '@/lib/optimizely';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { ResourceDownloadTemplateProps } from '../..';
import styled from './components.styled';

export interface ResourceDownloadFormProps {
  entry: ResourceDownloadTemplateProps['entry'];
  formSubmitted: boolean;
  setFormSubmitted: (submitted: boolean) => void;
  onToggleVideo?: (clicked: boolean) => void;
  onDownloadAsset?: (clicked: boolean) => void;
  config?: {
    isWebinar?: boolean;
    isWebinarUpcoming?: boolean;
    isProductFocused?: boolean;
    isWebinarReplay?: boolean;
    requireFormFill?: boolean;
    webinarType: CraftWebinarType;
  };
}

const ResourceDownloadForm: FC<ResourceDownloadFormProps> = ({
  entry,
  formSubmitted,
  setFormSubmitted,
  onToggleVideo,
  onDownloadAsset,
  config,
}) => {
  const { isWebinar, isWebinarUpcoming, isProductFocused, isWebinarReplay, webinarType, requireFormFill } =
    config ?? {};

  const { isGDPR } = useContext(OneTrustContext);
  // Marketing email opt in checkbox display value - we send the opposite of this value to as MarketingEmailOptOut to the backend
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(isGDPR ? false : true);

  useEffect(() => {
    setMarketingEmailOptIn(isGDPR ? false : true);
    {
      isGDPR && (
        formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, true)
      )
    }
  }, [isGDPR]);

  const [formError, setFormError] = useState<string | null>(null);

  const resourceAssetUrl = useMemo(() => {
    if (entry?.__typename === 'resources_resourceAsset_Entry') {
      return entry?.resourceAsset?.[0]?.url ?? entry?.resourceAssetLink ?? null;
    }

    return null;
  }, [entry]);

  const formHeadlineString: string = useMemo(() => {
    if (entry?.__typename == 'resources_resourceExternal_Entry') return '';

    if (entry?.formHeadline) {
      return entry?.formHeadline;
    } else if (isWebinar && isWebinarReplay) {
      return 'Watch webinar';
    } else if (isWebinar) {
      return 'Register now';
    } else {
      return 'Get your copy';
    }
  }, [entry, isWebinar, isWebinarReplay]);

  const formId = useMemo(() => {
    if (isWebinar && webinarType === 'regionalEvent') {
      return HubspotFormIDs.RegionalEvent;
    } else if (isWebinar && isWebinarReplay && isProductFocused) {
      return HubspotFormIDs.ResourceOnDemandProductWebinar;
    } else if (isWebinar && isWebinarReplay && !isProductFocused) {
      return HubspotFormIDs.ResourceOnDemandWebinar;
    } else if (isWebinar && !isWebinarReplay && isProductFocused) {
      return HubspotFormIDs.ResourceProductWebinar;
    } else if (isWebinar && !isWebinarReplay && !isProductFocused) {
      return HubspotFormIDs.ResourceWebinar;
    } else if (entry?.__typename === 'resources_resourceAsset_Entry') {
      return getResourceFormId(entry?.resourceType!);
    }

    /* Fallback */
    return HubspotFormIDs.ResourceEbook;
  }, [isWebinar, isWebinarReplay, isProductFocused]);

  const formik = useCustomFormik({
    initialValues: {
      [HubspotFieldNames.Email]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.JobTitle]: '',
      ...(isProductFocused
        ? {
          [HubspotFieldNames.Phone]: '',
          [HubspotFieldNames.CompanySize]: '',
        }
        : {}),
      ...(isWebinar && !isWebinarReplay
        ? {
          [HubspotFieldNames.WebinarFeedback]: '',
        }
        : {}),
      [HubspotFieldNames.MarketingEmailOptOut]: '',
    },
    onSubmit: useFormikHubspotSubmit({
      formId,
      name: 'Resource Form',
      content: entry?.__typename == 'resources_resourceExternal_Entry' ? '' : maybeNotSet(entry?.title),
      extraFields: {
        ...(isWebinar && !isWebinarReplay && entry?.__typename === 'resources_resourceWebinar_Entry'
          ? {
            [HubspotFieldNames.WebinarID]: entry.webinarId ?? '',
          }
          : {}),
      },
      beforeSubmit: (values) => {
        setFormError('');

        if (entry?.__typename === 'resources_resourceWebinar_Entry' && !!entry.webinarFeedback) {
          values[HubspotFieldNames.WebinarFeedback] = [
            entry?.webinarFeedback,
            values[HubspotFieldNames.WebinarFeedback],
          ].join(': ');
        }
      },
      onSuccess: (_values, _actions) => {
        setFormSubmitted(true);

        gtm.event(GTM_EVENTS.RESOURCE_FORM_SUCCESS);

        optimizely.event(OPTIMIZELY_EVENTS.RESOURCE_FORM_SUBMIT);
      },
      onError: (_errors, _values, _actions) => {
        setFormError('An unexpected error occurred, please try again.');
      },
    }),
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      if (isProductFocused) {
        errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
          values[HubspotFieldNames.Phone]!, // asserting it's not undefined since it's conditionally rendered
          {
            required: true,
          },
        );

        errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
          values[HubspotFieldNames.CompanySize]!, // asserting it's not undefined since it's conditionally rendered
          {
            required: true,
          },
        );
      }

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
  });

  useEffect(() => {
    if (formSubmitted && !!resourceAssetUrl && onDownloadAsset) {
      window.open(resourceAssetUrl, '_blank');
      onDownloadAsset(false);
    } else if (formSubmitted && isWebinar && isWebinarReplay && onToggleVideo) {
      onToggleVideo(false);
    }
  }, [formSubmitted]);

  if (requireFormFill && !formSubmitted) {
    return (
      <styled.FormWrapper>
        <styled.FormHeadline>{formHeadlineString}</styled.FormHeadline>

        {isWebinar && !isWebinarReplay && !isWebinarUpcoming && (
          <p>Check back soon for the recording of this webinar!</p>
        )}

        {!(isWebinar && !isWebinarUpcoming && !isWebinarReplay) && (
          <styled.Form onSubmit={formik.handleSubmit}>
            <InputField
              type="email"
              name={HubspotFieldNames.Email}
              label={HubspotFieldLabels.Email}
              placeholder="you@company.com"
              value={formik.values[HubspotFieldNames.Email]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched[HubspotFieldNames.Email] && !!formik.errors[HubspotFieldNames.Email]}
              errorMessage={formik.errors[HubspotFieldNames.Email] as string}
            />

            <styled.FormDetailsWrapper>
              <InputField
                type="text"
                name={HubspotFieldNames.FirstName}
                label={HubspotFieldLabels.FirstName}
                placeholder="Jamie"
                value={formik.values[HubspotFieldNames.FirstName]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[HubspotFieldNames.FirstName] && !!formik.errors[HubspotFieldNames.FirstName]}
                errorMessage={formik.errors[HubspotFieldNames.FirstName] as string}
              />

              <InputField
                type="text"
                name={HubspotFieldNames.LastName}
                label={HubspotFieldLabels.LastName}
                placeholder="Smith"
                value={formik.values[HubspotFieldNames.LastName]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[HubspotFieldNames.LastName] && !!formik.errors[HubspotFieldNames.LastName]}
                errorMessage={formik.errors[HubspotFieldNames.LastName] as string}
              />

              <InputField
                type="text"
                name={HubspotFieldNames.CompanyName}
                label={HubspotFieldLabels.CompanyName}
                placeholder="Your Company"
                value={formik.values[HubspotFieldNames.CompanyName]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched[HubspotFieldNames.CompanyName] && !!formik.errors[HubspotFieldNames.CompanyName]
                }
                errorMessage={formik.errors[HubspotFieldNames.CompanyName] as string}
              />

              <InputField
                type="text"
                name={HubspotFieldNames.JobTitle}
                label={HubspotFieldLabels.JobTitle}
                placeholder="e.g., Director of Operations"
                value={formik.values[HubspotFieldNames.JobTitle]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[HubspotFieldNames.JobTitle] && !!formik.errors[HubspotFieldNames.JobTitle]}
                errorMessage={formik.errors[HubspotFieldNames.JobTitle] as string}
              />

              {isProductFocused && (
                <>
                  <PhoneField
                    name={HubspotFieldNames.Phone}
                    label={HubspotFieldLabels.Phone}
                    value={formik.values[HubspotFieldNames.Phone]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.Phone] && !!formik.errors[HubspotFieldNames.Phone]}
                    errorMessage={formik.errors[HubspotFieldNames.Phone] as string}
                  />

                  <SelectField
                    name={HubspotFieldNames.CompanySize}
                    label={HubspotFieldLabels.CompanySize}
                    value={formik.values[HubspotFieldNames.CompanySize]}
                    options={HubspotCompanySizeDropdownOptions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.CompanySize] && !!formik.errors[HubspotFieldNames.CompanySize]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.CompanySize] as string}
                  />
                </>
              )}

              {isWebinar &&
                !isWebinarReplay &&
                entry?.__typename === 'resources_resourceWebinar_Entry' &&
                !!entry.webinarFeedback && (
                  <InputField
                    type="text"
                    name={HubspotFieldNames.WebinarFeedback}
                    label={entry.webinarFeedback}
                    value={formik.values[HubspotFieldNames.WebinarFeedback]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.WebinarFeedback] &&
                      !!formik.errors[HubspotFieldNames.WebinarFeedback]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.WebinarFeedback] as string}
                  />
                )}
            </styled.FormDetailsWrapper>

            <styled.ButtonWrapper>
              {!!formError && <styled.FormError>{formError}</styled.FormError>}

              {isGDPR && (
                <Shared.CheckboxWrapper>
                  <Checkbox
                    id={HubspotFieldNames.MarketingEmailOptOut}
                    checked={marketingEmailOptIn}
                    onToggle={(checked) => {
                      // Since we're using the same field for both the signup and demo modal, we need to invert the value since we're opting IN rather than opting out
                      setMarketingEmailOptIn(checked);
                      formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, !checked);
                    }}
                  />
                  <label htmlFor={HubspotFieldNames.MarketingEmailOptOut}>
                    Opt into emails with the latest news, offers, promotions, and resources from Front.
                  </label>
                </Shared.CheckboxWrapper>
              )}

              <TermsAndPrivacyNotice /><br /><br />

              <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting}>
                {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
              </PrimaryInnerGlowButton>
            </styled.ButtonWrapper>
          </styled.Form>
        )}
      </styled.FormWrapper>
    );
  }

  return (
    <styled.FormWrapper>
      <styled.FormSubmittedWrapper>
        {isWebinar ? (
          isWebinarReplay ? (
            <>
              {requireFormFill && <styled.FormHeadline>Thank you!</styled.FormHeadline>}

              <p>Click here to watch your video.</p>
              <span>
                <PrimaryInnerGlowButton onClick={() => onToggleVideo && onToggleVideo(true)}>
                  Watch video
                </PrimaryInnerGlowButton>
              </span>
            </>
          ) : requireFormFill ? (
            <>
              <styled.FormHeadline>Thank you for registering!</styled.FormHeadline>
              <p>We&rsquo;re sending you a confirmation email.</p>
            </>
          ) : null
        ) : !!resourceAssetUrl ? (
          <>
            {requireFormFill && <styled.FormHeadline>Thank you!</styled.FormHeadline>}

            <p>Your download is ready for you.</p>

            <span>
              <PrimaryInnerGlowButton
                as={'a'}
                href={resourceAssetUrl}
                onClick={() => onDownloadAsset && onDownloadAsset(true)}
                target="_blank"
                download
              >
                Download
              </PrimaryInnerGlowButton>
            </span>
          </>
        ) : null}
      </styled.FormSubmittedWrapper>
    </styled.FormWrapper>
  );
};

export default ResourceDownloadForm;
